import React, { useState, useEffect } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  Button,
  Card,
  Divider,
  Paper,
  Grid,
  Typography,
  TextField,
  Box,
  CircularProgress,
  useTheme,
  TablePagination,
  IconButton,
  Modal,
  Chip,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { toast, ToastContainer } from "react-toastify";
import { useFormik } from "formik";
import Autocomplete from "@mui/material/Autocomplete";
import { useTranslation } from "react-i18next";
import PrintIcon from "@mui/icons-material/Print";
import DescriptionIcon from "@mui/icons-material/Description";
import { styled } from "@mui/material/styles";
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  FirstPage,
  LastPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from "@mui/icons-material";
import api from "../../../utils/Url";
import moment from "moment";
import { set } from "date-fns";

interface PDFPreviewModalProps {
  open: boolean;
  onClose: () => void;
  pdfBlob: Blob | null;
  onDownload: () => void;
}
interface FileDocument {
  fileID: number;
  fileName: string;
  fileNo: string;
  fileType: number;
  fileGroup: number;
  fileCategory: number;
  fileClass: number;
  fileSubject: number;
  synopsis: string;
  remark: string;
  refID: number;
  totalPages: number;
  fromPage: number;
  toPage: number;
  status: string;
  originLocation: string;
  currentLocation: string;
  zoneID: number;
  wardID: number;
  createdDate: string;
  createdBy: string;
  updatedBy: string;
  updatedDate: string;
  rfid: string;
  senderID: string;
  receiverID: string;
  fileDoc: string; // This will contain the base64 encoded file
}
// Custom pagination actions component
function TablePaginationActions(props: {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number
  ) => void;
}) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPage /> : <FirstPage />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPage /> : <LastPage />}
      </IconButton>
    </Box>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 600,
    fontSize: "0.875rem",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "0.875rem",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.selected,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));


interface FileDetailsModalProps {
  open: boolean;
  onClose: () => void;
  fileData: any;
}

const FileDetailsModal = ({ open, onClose, fileData }: FileDetailsModalProps) => {
 const { t } = useTranslation();
   const [locationData, setLocationData] = useState<any>(null);
  const [loadingLocation, setLoadingLocation] = useState(false);


   useEffect(() => {
    if (open && fileData?.currentLocation) {
      fetchLocationDetails(fileData.currentLocation);
    }
  }, [open, fileData?.currentLocation]);

  const fetchLocationDetails = async (locationId: number) => {
    try {
      setLoadingLocation(true);
      const response = await api.post("/LocationItems/GetMappedLocation", { id: locationId });
      if (response.data.isSuccess && response.data.data.length > 0) {
        setLocationData(response.data.data[0]);
      } else {
        setLocationData(null);
      }
    } catch (error) {
      console.error("Error fetching location details:", error);
      setLocationData(null);
    } finally {
      setLoadingLocation(false);
    }
  };

    const getLocationPath = () => {
    if (!locationData) return "N/A";
    
    // You can customize this based on how you want to display the path
    return locationData.location  || "N/A";
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="file-details-modal"
      aria-describedby="file-details"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '95vw', sm: '85vw', md: '75vw', lg: '65vw', xl: '55vw' },
        maxHeight: '90vh',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 4,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header with gradient background */}
        <Box sx={{
          background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
          color: 'white',
          p: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              {t("text.FileName")}: {fileData?.fileName || t("text.FileDetails")}
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              {t("text.FileNo")}: {fileData?.fileNo || 'N/A'} | {t("text.RFID")}: {fileData?.rfid || 'N/A'}
            </Typography>
          </Box>
          <IconButton 
            onClick={onClose} 
            sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>
        
        {/* Main content with scrollable area */}
        <Box sx={{ p: 3, overflowY: 'auto', flex: 1 }}>
          <Grid container spacing={3}>
            {/* Basic Information Section */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 500 }}>
                  {t("text.BasicInformation")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <DetailItem label={t("text.FileType")} value={fileData?.fileTypeName} />
                <DetailItem label={t("text.FileGroup")} value={fileData?.fileGroupName} />
                <DetailItem label={t("text.Category")} value={fileData?.fileCategoryName} />
                <DetailItem label={t("text.Class")} value={fileData?.className} />
                <DetailItem label={t("text.Subject")} value={fileData?.fileSubjectName} />
                <DetailItem label={t("text.chackNo")} value={fileData?.chackNo} />
                <DetailItem label={t("text.TotalPages")} value={fileData?.totalPages} />
                <DetailItem label={t("text.PagesRange")} value={`${fileData?.fromPage || '1'} - ${fileData?.toPage || '10'}`} />
              </Paper>
            </Grid>
            
            {/* Status & Location Section */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 500 }}>
                  {t("text.StatusLocation")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <DetailItem 
                  label={t("text.Status")} 
                  value={
                    <Chip 
                      label={
                        fileData?.status === "1" ? t("text.Received") :
                        fileData?.status === "2" ? t("text.Pending") :
                        fileData?.status === "3" ? t("text.Forwarded") : t("text.NA")
                      }
                      color={
                        fileData?.status === "1" ? "success" :
                        fileData?.status === "2" ? "warning" :
                        fileData?.status === "3" ? "info" : "default"
                      }
                      size="small"
                    />
                  } 
                />
                  <DetailItem 
                  label={t("text.Location")} 
                  value={
                    loadingLocation ? (
                      <CircularProgress size={20} />
                    ) : (
                      locationData ? (
                        <>
                         
                          <Typography variant="body2" sx={{ color: 'text.primary', mt: 0.5 }}>
                            {getLocationPath()}
                          </Typography>
                       
                        </>
                      ) : (
                        "N/A"
                      )
                    )
                  } 
                />
                {/* <DetailItem label={t("text.CurrentLocation")} value={fileData?.currentLocation} /> */}
                <DetailItem label={t("text.Zone")} value={fileData?.zoneName} />
                <DetailItem label={t("text.Ward")} value={fileData?.wardName} />
                <DetailItem label={t("text.Registrar")} value={fileData?.registrarName} />
              </Paper>
            </Grid>
            
            {/* Dates & Tracking Section */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 500 }}>
                  {t("text.DatesTracking")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <DetailItem label={t("text.CreatedDate")} value={formatDate(fileData?.createdDate)} />
                <DetailItem label={t("text.UpdatedDate")} value={formatDate(fileData?.updatedDate)} />
                <DetailItem label={t("text.ProposalNo")} value={fileData?.proposalNo} />
                <DetailItem label={t("text.ProposalDate")} value={formatDate(fileData?.proposalDate)} />
              </Paper>
            </Grid>
            
            {/* Additional Information Section */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 500 }}>
                  {t("text.AdditionalInformation")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <DetailItem label={t("text.Synopsis")} value={fileData?.synopsis} />
                <DetailItem 
                  label={t("text.Remark")} 
                  value={
                    <Typography sx={{ 
                      fontStyle: fileData?.remark ? 'normal' : 'italic',
                      color: fileData?.remark ? 'text.primary' : 'text.secondary'
                    }}>
                      {fileData?.remark || t("text.NoRemarksProvided")}
                    </Typography>
                  } 
                />
              </Paper>
            </Grid>
          </Grid>
        </Box>
        
      
      </Box>
    </Modal>
  );
};

// Helper component for consistent detail items
const DetailItem = ({ label, value }: { label: string; value?: React.ReactNode }) => {
   const { t } = useTranslation();
  
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
        {label}
      </Typography>
      {value ? (
        typeof value === 'string' || typeof value === 'number' ? (
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {value || t("text.NA")}
          </Typography>
        ) : (
          value
        )
      ) : (
        <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
          {t("text.NA")}
        </Typography>
      )}
    </Box>
  );
};

// Helper function to format dates
const formatDate = (dateString?: string) => {
  if (!dateString) return "NA";
  return moment.utc(dateString).format("DD/MM/YYYY");
};


function Row({ row, index }: { row: any; index: number }) {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileDocument, setFileDocument] = useState<FileDocument | null>(null);

  const fetchFileDocument = async (fileID: number) => {
    setFileLoading(true);
    try {
      const response = await api.post('FileDetail/GetFileDetails', {
        ZoneID: -1,
        wardID: -1,
        fileID: fileID,
        fileType: -1,
        fileGroup: -1,
        fileCategory: -1,
        fileClass: -1,
        fileSubject: -1
      });
      
      if (response.data.isSuccess && response.data.data && response.data.data.length > 0) {
        setFileDocument(response.data.data[0]);
        setFileModalOpen(true);
      }
    } catch (err) {
      console.error("Error fetching file document:", err);
    } finally {
      setFileLoading(false);
    }
  };

  const renderFileContent = () => {
    if (!fileDocument || !fileDocument.fileDoc) {
      return <Typography>{t("text.NoFileContentAvailable")}</Typography>;
    }

    const [mimeType, base64Data] = fileDocument.fileDoc.split(';base64,');
    
    if (mimeType.includes('application/pdf')) {
      return (
        <embed 
          src={fileDocument.fileDoc} 
          type="application/pdf" 
          width="100%" 
          height="100%"
          style={{ minHeight: '500px' }}
        />
      );
    } else if (mimeType.includes('image/')) {
      return (
        <img 
          src={fileDocument.fileDoc} 
          alt={fileDocument.fileName}
          style={{ maxWidth: '100%', maxHeight: '100%' }}
        />
      );
    } else {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">{fileDocument.fileName}</Typography>
          <Typography>{t("text.UnsupportedFileFormat")}</Typography>
          <Button 
            variant="contained" 
            href={fileDocument.fileDoc} 
            download={fileDocument.fileName}
            sx={{ mt: 2 }}
          >
            {t("text.DownloadFile")}
          </Button>
        </Box>
      );
    }
  };

  return (
    <>
      <StyledTableRow>
        <StyledTableCell align="center">{index + 1}</StyledTableCell>
        <StyledTableCell>
          <Typography 
            onClick={() => setModalOpen(true)}
            sx={{
              color: 'primary.main',
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': {
                color: 'primary.dark',
              }
            }}
          >
            {row.fileName || "NA"}
          </Typography>
        </StyledTableCell>
        <StyledTableCell align="center">{row.fileNo || "NA"}</StyledTableCell>
        <StyledTableCell align="center">{row.chackNo || "NA"}</StyledTableCell>
        <StyledTableCell align="center">{row.rfid || "NA"}</StyledTableCell>
        <StyledTableCell align="center">
          {row.status === "1"
            ? "Received"
            : row.status === "2"
            ? "Pending"
            : row.status === "3"
            ? "Forwarded"
            : row.status === ""
            ? "NA"
            : row.status}
        </StyledTableCell>
        <StyledTableCell align="center">{row.remark || "NA"}</StyledTableCell>
        {/* Add the preview button cell */}
        <StyledTableCell align="center">
          <IconButton
            onClick={() => fetchFileDocument(row.fileID)}
            color="primary"
            size="small"
            disabled={fileLoading}
            sx={{ '&:hover': { backgroundColor: 'transparent' } }}
          >
            {fileLoading ? <CircularProgress size={24} /> : <VisibilityIcon />}
          </IconButton>
        </StyledTableCell>
      </StyledTableRow>
      
      <FileDetailsModal 
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fileData={row}
      />

      {/* File Preview Modal */}
      <Modal
        open={fileModalOpen}
        onClose={() => setFileModalOpen(false)}
        aria-labelledby="file-preview-modal"
        aria-describedby="file-preview"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(2px)',
        }}
      >
        <Box sx={{
          width: { xs: '95vw', sm: '85vw', md: '75vw', lg: '65vw' },
          height: { xs: '85vh', sm: '80vh', md: '75vh' },
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 3,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '4px solid transparent',
          backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
          backgroundOrigin: 'border-box',
          backgroundClip: 'content-box, border-box',
          position: 'relative',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: -1,
            margin: '-4px',
            borderRadius: 'inherit',
            background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
          }
        }}>
          {/* Header with gradient background */}
          <Box sx={{
            background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
            color: 'white',
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t("text.PreviewFile")} - {fileDocument?.fileName}
            </Typography>
            <IconButton 
              onClick={() => setFileModalOpen(false)} 
              sx={{ 
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          
          {/* File Content */}
          <Box sx={{ 
            flex: 1, 
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f5f5f5',
            p: 2
          }}>
            {renderFileContent()}
          </Box>
        </Box>
      </Modal>
    </>
  );
}

const PDFPreviewModal = ({ open, onClose, pdfBlob, onDownload }: PDFPreviewModalProps) => {
  const { t } = useTranslation();
  const [objectURL, setObjectURL] = useState<string | null>(null);

  useEffect(() => {
    if (pdfBlob && open) {
      const url = URL.createObjectURL(pdfBlob);
      setObjectURL(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [pdfBlob, open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="pdf-preview-modal"
      aria-describedby="pdf-preview"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(2px)',
      }}
    >
      <Box sx={{
        width: { xs: '95vw', sm: '85vw', md: '75vw', lg: '65vw' },
        height: { xs: '85vh', sm: '80vh', md: '75vh' },
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: '4px solid transparent',
        backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
        backgroundOrigin: 'border-box',
        backgroundClip: 'content-box, border-box',
        position: 'relative',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          zIndex: -1,
          margin: '-4px',
          borderRadius: 'inherit',
          background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
        }
      }}>
        {/* Header with gradient background */}
        <Box sx={{
          background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
          color: 'white',
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t("text.PDFPreview")}
          </Typography>
          <IconButton 
            onClick={onClose} 
            sx={{ 
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* PDF Content */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#f5f5f5',
          position: 'relative',
          '& iframe': {
            width: 'calc(100% + 40px)', // Extend beyond container
            height: 'calc(100% + 40px)', // Extend beyond container
            border: 'none',
            margin: '-20px', // Pull the iframe outward
            // Hide the PDF viewer controls
            '& .pdfViewer .toolbar': {
              display: 'none !important',
            },
            '& .pdfViewer .findbar': {
              display: 'none !important',
            },
            '& .secondaryToolbar': {
              display: 'none !important',
            }
          }
        }}>
          {objectURL ? (
            <iframe 
              src={`${objectURL}#toolbar=0&navpanes=0`}
              style={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
              title="PDF Preview"
            />
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              gap: 2
            }}>
              <CircularProgress size={60} thickness={4} />
              <Typography variant="body1" color="text.secondary">
                {t("text.LoadingPDF")}...
              </Typography>
            </Box>
          )}
        </Box>
        
        {/* Footer with actions */}
        <Box sx={{
          p: 2,
          bgcolor: 'grey.50',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Button 
            variant="outlined" 
            onClick={onClose}
            sx={{ 
              minWidth: 120,
              fontWeight: 600,
              borderRadius: 1,
              textTransform: 'none'
            }}
          >
            {t("text.Cancel")}
          </Button>
          <Button 
            variant="contained" 
            onClick={onDownload}
            sx={{ 
              minWidth: 120,
              fontWeight: 600,
              borderRadius: 1,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #303f9f 0%, #1976d2 100%)',
              }
            }}
          >
            {t("text.Download")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default function FileDetailsReports() {
    const [error, setError] = useState<string | null>(null);
     const [fileDocument, setFileDocument] = useState<FileDocument | null>(null);
      const [fileModalOpen, setFileModalOpen] = useState(false);
      const [fileLoading, setFileLoading] = useState(false)
  const { t } = useTranslation();
  const theme = useTheme();
const [pdfModalOpen, setPdfModalOpen] = useState(false);
const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
const [pdfDownloadFn, setPdfDownloadFn] = useState<(() => void) | null>(null);
  const [data, setData] = useState<any>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [optionFileCategory, setOptionFileCategory] = useState([
    { value: "-1", label: t("text.SelectFileCategory") },
  ]);
  const [optionFileGroup, setOptionFileGroup] = useState([
    { value: "-1", label: t("text.SelectFileGroup") },
  ]);
  const [optionFileClass, setOptionFileClass] = useState([
    { value: "-1", label: t("text.SelectFileClass") },
  ]);
  const [optionFilesubject, setOptionFilesubject] = useState([
    { value: "-1", label: t("text.SelectFileSubject") },
  ]);
  const [optionFileType, setOptionFileType] = useState([
    { value: "-1", label: t("text.SelectFileType") },
  ]);
  const [optionFileDetailsAll, setOptionFileDetailsAll] = useState([
    { value: "-1", label: t("text.SelectFile") },
  ]);
  const [optionchackNo, setOptionchackNo] = useState([
    { label: t("text.Selectchack") },
  ]);
 const [allWards, setAllWards] = useState([]); // Store all wards
  const [ZoneOption, setZoneOption] = useState([{ value: "-1", label: t("text.SelectZone") }]);
  const [WardOption, setWardOption] = useState([{ value: "-1", label: t("text.SelectWard") }]);
    const [optionRefid, setOptionRefid] = useState([
      { label: t("text.SelectRFID") },
    ]);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    getFileCategoryData();
    getFileGroupData();
    getFileClassData();
    getFileSubjectData();
    getFileTypeData();
    getFileDtailsAllData();
   // getchackData();
       getZone();
    getWard(); 
  }, []);

  const handleZoneChange = (event: any, newValue: any) => {
    console.log(newValue?.value);

    formik.setFieldValue("zoneID", newValue?.value);
   // formik.setFieldValue("zoneName", newValue?.label);
    formik.setFieldTouched("zoneID", true);
    formik.setFieldTouched("zoneID", false);

    // Reset ward selection when zone changes
    formik.setFieldValue("wardID", -1);
   // formik.setFieldValue("wardName", "");

    if (newValue?.value && newValue.value !== "-1") {
      // Filter wards based on selected zone
      const filteredWards = allWards.filter((ward: any) => ward.zoneID === newValue.value);
      setWardOption([{ value: "-1", label: t("text.SelectWard") }, ...filteredWards]);
    } else {
      // Show all wards if no zone is selected or "Select Zone" is chosen
      setWardOption([{ value: "-1", label: t("text.SelectWard") }, ...allWards]);
    }
  };

  const getWard = () => {
    const collectData = {
      wardID: -1,
      zoneID: -1, // Get all wards initially
      user_ID: parseInt(ID),
    };
    api
      .post(`Ward/GetWardmaster`, collectData)
      .then((res) => {
        const arr: any = [];
        console.log("result" + JSON.stringify(res.data.data));
        for (let index = 0; index < res.data.data.length; index++) {
          const wardName = res.data.data[index]["wardName"];
          const wardCode = res.data.data[index]["wardCode"];
          arr.push({
            label: `${wardName} (${wardCode})`,
            value: res.data.data[index]["wardID"],
            zoneID: res.data.data[index]["zoneID"] // Store zoneID with each ward
          });
        }
        setAllWards(arr); // Store all wards
        setWardOption([{ value: "-1", label: t("text.SelectWard") }, ...arr]); // Initially show all wards
      });
  };

    let ID: any = localStorage.getItem("useR_ID");
  if (ID !== null) {
    ID = ID.replace(/\D/g, "");
  }
  const getZone = () => {
    const collectData = {
      zoneID: -1,
      user_ID: parseInt(ID),
      isActive: true
    };
    api
      .post(`Zone/GetZonemaster`, collectData)
      .then((res) => {
        const arr: any = [];
        console.log("result" + JSON.stringify(res.data.data));
        for (let index = 0; index < res.data.data.length; index++) {
          arr.push({
            label: res.data.data[index]["zoneName"],
            value: res.data.data[index]["zoneID"]
          });
        }
        setZoneOption([{ value: "-1", label: t("text.SelectZone") }, ...arr]);
      });
  };
  


  const getFileCategoryData = () => {
    const collectData = {
      fileCatid: -1,
    };

    api.post("FileCategory/GetFileCategory", collectData).then((res) => {
      const arr = [];
      for (let index = 0; index < res.data.data.length; index++) {
        arr.push({
          label: res.data.data[index]["fileCatDesc"],
          value: res.data.data[index]["fileCatid"],
        });
      }
      setOptionFileCategory(arr);
    });
  };

  const getFileGroupData = () => {
    const collectData = {
      fileGroupid: -1,
    };

    api.post("FileGroup/GetFileGroup", collectData).then((res) => {
      const arr = [];
      for (let index = 0; index < res.data.data.length; index++) {
        arr.push({
          label: res.data.data[index]["fileGroupName"],
          value: res.data.data[index]["fileGroupid"],
        });
      }
      setOptionFileGroup(arr);
    });
  };

  const getFileClassData = () => {
    const collectData = {
      fileClassid: -1,
    };

    api.post("FileClass/GetFileClass", collectData).then((res) => {
      const arr = [];
      for (let index = 0; index < res.data.data.length; index++) {
        arr.push({
          label: res.data.data[index]["classDescription"],
          value: res.data.data[index]["fileClassid"],
        });
      }
      setOptionFileClass(arr);
    });
  };

  const getFileSubjectData = () => {
    const collectData = {
      id: -1,
    };

    api.post("FileSubject/GetFileSubject", collectData).then((res) => {
      const arr = [];
      for (let index = 0; index < res.data.data.length; index++) {
        arr.push({
          label: res.data.data[index]["fileSubjectName"],
          value: res.data.data[index]["id"],
        });
      }
      setOptionFilesubject(arr);
    });
  };

  const getFileTypeData = () => {
    const collectData = {
      fileTypeId: -1,
    };

    api.post("FileType/GetFileType", collectData).then((res) => {
      const arr = [];
      for (let index = 0; index < res.data.data.length; index++) {
        arr.push({
          label: res.data.data[index]["fileTypeName"],
          value: res.data.data[index]["fileTypeId"],
        });
      }
      setOptionFileType(arr);
    });
  };


const getFileDtailsAllData = () => {
    const collectData = {"fileID":-1,"chackNo":"","fileType":-1,"fileGroup":-1,"fileCategory":-1,"fileClass":-1,"fileSubject":-1,"zoneID":-1,"wardID":-1,"rfid":"","user_ID":""}
    api.post("FileDetail/GetFileDetails", collectData).then((res) => {
      const arr = [];
      const arr1: any = [];
      const arr2: any = [];

      for (let index = 0; index < res.data.data.length; index++) {
        arr.push({
          label: res.data.data[index]["fileNo"] + " - " + res.data.data[index]["fileName"],
          value: res.data.data[index]["fileID"],
        });
      }

      for (let index = 0; index < res.data.data.length; index++) {
        const rfid = res.data.data[index]["rfid"];
        const refID = res.data.data[index]["rfid"];

        if (rfid !== "" && rfid !== 0) {
          
          arr1.push({
            label: rfid,
            value: rfid,
          });
        }
      }

  for (let index = 0; index < res.data.data.length; index++) {
        const chackNo = res.data.data[index]["chackNo"];
     //   const chackNo = res.data.data[index]["chackNo"];

        if (chackNo !== "" && chackNo !== 0) {
          
          arr2.push({
            label: chackNo,
            value: chackNo,
          });
        }
      }
setOptionchackNo(arr2);
      setOptionRefid(arr1);
      setOptionFileDetailsAll(arr);
    });
  };

    const fetchFileDocument = async (fileID: number) => {
    setFileLoading(true);
    try {
      const response = await api.post('FileDetail/GetFileDetails', {
        ZoneID: -1,
        wardID: -1,
        fileID: fileID,
        fileType: -1,
        fileGroup: -1,
        fileCategory: -1,
        fileClass: -1,
        fileSubject: -1
      });
      
      if (response.data.isSuccess && response.data.data && response.data.data.length > 0) {
        setFileDocument(response.data.data[0]);
        setFileModalOpen(true);
      } else {
        throw new Error(t("text.FileContentNotFound"));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("text.FailedToLoadFileContent"));
    } finally {
      setFileLoading(false);
    }
  };

  interface FormValues {
    fileID: any;
    fileType: any;
    chackNo: string;
    fileGroup: any;
    fileCategory: any;
    fileClass: any;
    fileSubject: any;
    zoneID?: any;
    wardID?: any;
    rfid?: string;
    user_ID?: string;
  }

  const initialValues: FormValues = {
   
    "fileID": -1,
  "chackNo": "",
  "fileType": -1,
  "fileGroup": -1,
  "fileCategory": -1,
  "fileClass": -1,
  "fileSubject": -1,
  "zoneID": -1,
  "wardID": -1,
  "rfid": "",
  "user_ID": ""

  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setHasSearched(true);
        setPage(0); // Reset to first page when new search is performed
        
        if (values.fileID === initialValues.fileID) {
          values.fileID = -1;
        }
        if (values.fileType === initialValues.fileType) {
          values.fileType = -1;
        }
        if (values.fileGroup === initialValues.fileGroup) {
          values.fileGroup = -1;
        }
        if (values.fileCategory === initialValues.fileCategory) {
          values.fileCategory = -1;
        }
        if (values.fileClass === initialValues.fileClass) {
          values.fileClass = -1;
        }
        if (values.fileSubject === initialValues.fileSubject) {
          values.fileSubject = -1;
        }
        if (values.chackNo === initialValues.chackNo) {
          values.chackNo = "";
        }
          if (values.rfid === initialValues.rfid) {
          values.rfid = "";
        }
            if (values.zoneID === initialValues.zoneID) {
          values.zoneID = -1;
        }
            if (values.wardID === initialValues.wardID) {
          values.wardID = -1;
        }

        const response = await api.post(`FileDetail/GetFileDetails`, values);
        setData(response.data.data);
        if (response.data.isSuccess) {
          // toast.success(response.data.mesg);
        } else {
          // toast.error(response.data.mesg);
        }
      } catch (error) {
        toast.error("An error occurred while fetching data");
      } finally {
        setLoading(false);
      }
    },
  });

const exportToPDF = () => {
  if (data.length === 0) {
    toast.warning(t("text.NoDataToExport"));
    return;
  }

  // Create a new PDF document in landscape
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4"
  });

  // Add title and metadata
  doc.setProperties({
    title: "File Details Report",
    subject: "File Information",
    author: "Your Application",
  });

  // Add header with title
  doc.setFontSize(20);
  doc.setTextColor(63, 81, 181);
  doc.setFont("helvetica", "bold");
  doc.text("FILE DETAILS REPORT", doc.internal.pageSize.width / 2, 20, { align: "center" });

  // Add generated timestamp
  doc.setFontSize(10);
  doc.setTextColor(120, 120, 120);
  doc.text(
    `Generated on: ${moment.utc().format('DD/MM/YYYY')}`,
    doc.internal.pageSize.width / 2,
    27,
    { align: "center" }
  );

  // Add a horizontal line separator
  doc.setDrawColor(200, 200, 200);
  doc.line(15, 32, doc.internal.pageSize.width - 15, 32);

  // Prepare data for the table
  const headers = [
    { content: "Sr No", styles: { halign: 'center', cellWidth: 20 } },
    { content: "File Name", styles: { halign: 'center', cellWidth: 35 } },
    { content: "File No", styles: { halign: 'center', cellWidth: 20 } },
    { content: "chack No", styles: { halign: 'center', cellWidth: 60 } },
    { content: "RFID", styles: { halign: 'center', cellWidth: 60 } },
    { content: "Status", styles: { halign: 'center', cellWidth: 30 } },
    { content: "Remark", styles: { halign: 'center', cellWidth: 40 } }
  ];

  const tableData = data.map((row: any, index: number) => [
    { content: index + 1, styles: { halign: 'center' } },
    row.fileName || "N/A",
    row.fileNo || "N/A",
    row.chackNo || "N/A",
    row.rfid || "N/A",
    row.status === "1" ? "Received" : 
    row.status === "2" ? "Pending" : 
    row.status === "3" ? "Forwarded" : "N/A",
    row.remark || "N/A"
  ]);

  // Add the table using autoTable
  autoTable(doc, {
    startY: 38,
    head: [headers.map(h => h.content)],
    body: tableData,
    theme: "grid",
    headStyles: {
      fillColor: [63, 81, 181],
      textColor: 255,
      fontStyle: "bold",
      halign: 'center'
    },
    alternateRowStyles: {
      fillColor: [248, 248, 248],
    },
    styles: {
      cellPadding: 4,
      fontSize: 10,
      valign: 'middle',
      overflow: 'linebreak',
      minCellHeight: 8,
      lineColor: [220, 220, 220],
      lineWidth: 0.3
    },
    columnStyles: headers.reduce((acc: any, header, index) => {
      acc[index] = header.styles;
      return acc;
    }, {}),
    margin: { left: 15, right: 15 },
    tableWidth: 'auto'
  });

  // Add professional footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    
    doc.text(
      "© 2025 MSSPL. All rights reserved.",
      15,
      doc.internal.pageSize.height - 10
    );
    
    doc.text(
      "CONFIDENTIAL",
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: "center" }
    );
    
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width - 15,
      doc.internal.pageSize.height - 10,
      { align: "right" }
    );
  }

  // Generate PDF blob
  const pdfBlob = doc.output('blob');
  setPdfBlob(pdfBlob);

  // Set up download function
  const downloadFn = () => {
    doc.save(`File_Details_Report_${new Date().toISOString().slice(0, 19).replace(/[:T-]/g, '')}.pdf`);
    setPdfModalOpen(false);
  };
  setPdfDownloadFn(() => downloadFn);

  // Open preview modal
  setPdfModalOpen(true);
}

const exportToExcel = () => {
  if (data.length === 0) {
    toast.warning(t("text.NoDataToExport"));
    return;
  }

  // Prepare the worksheet data
  const excelData = data.map((row: any, index: number) => ({
    "Sr No": index + 1,
    "File Name": row.fileName || "N/A",
    "File No": row.fileNo || "N/A",
    "chack No": row.chackNo || "N/A",
    "RFID": row.rfid || "N/A",
    "Status": row.status === "1" ? "Received" : 
              row.status === "2" ? "Pending" : 
              row.status === "3" ? "Forwarded" : "N/A",
    "Remark": row.remark || "N/A"
  }));

  // Create a new workbook
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(excelData);

  // Calculate maximum width for each column
  const colWidths = [
    { wch: 8 },  // Sr No (fixed width)
  ];

  // Calculate dynamic widths for other columns
  const excelHeaders = ["File Name", "File No", "chack No", "RFID", "Status", "Remark"];
  excelHeaders.forEach((header, colIndex) => {
    // Start with header width
    let maxWidth = header.length * 1.3; // Character width factor
    
    // Check all data rows for this column
    excelData.forEach((row: { [x: string]: { toString: () => string; }; }) => {
      const value = row[header]?.toString() || "";
      const cellWidth = value.length * 1.1; // Slightly less than header factor
      if (cellWidth > maxWidth) {
        maxWidth = cellWidth;
      }
    });

    // Apply minimum and maximum limits
    maxWidth = Math.max(12, Math.min(maxWidth, 50)); // Min 12, Max 50
    colWidths.push({ wch: maxWidth });
  });

  // Set column widths
  ws["!cols"] = colWidths;

  // Style definitions
  const headerStyle = {
    font: {
      bold: true,
      color: { rgb: "FFFFFF" },
      sz: 12,
      name: "Calibri"
    },
    fill: {
      fgColor: { rgb: "203864" } // Navy blue
    },
    border: {
      top: { style: "medium", color: { rgb: "1F497D" } },
      bottom: { style: "medium", color: { rgb: "1F497D" } },
      left: { style: "medium", color: { rgb: "1F497D" } },
      right: { style: "medium", color: { rgb: "1F497D" } }
    },
    alignment: {
      horizontal: "center",
      vertical: "center",
      wrapText: true
    }
  };

  const cellStyle = {
    font: {
      sz: 11,
      name: "Calibri"
    },
    border: {
      top: { style: "thin", color: { rgb: "D9D9D9" } },
      bottom: { style: "thin", color: { rgb: "D9D9D9" } },
      left: { style: "thin", color: { rgb: "D9D9D9" } },
      right: { style: "thin", color: { rgb: "D9D9D9" } }
    },
    alignment: {
      vertical: "center"
    }
  };

  // Apply styles to all cells
  const range = XLSX.utils.decode_range(ws["!ref"] || "A1:G" + (excelData.length + 1));
  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      if (!ws[cellAddress]) ws[cellAddress] = {};
      
      if (R === 0) {
        // Header row
        ws[cellAddress].s = headerStyle;
      } else {
        // Data rows
        ws[cellAddress].s = {
          ...cellStyle,
          alignment: {
            ...cellStyle.alignment,
            horizontal: C === 0 || C === 2 || C === 3 || C === 4 ? "center" : "left" // Center align numeric/status columns
          }
        };
        
        // Color status column
        if (C === 5) {
          const status = ws[cellAddress].v;
          if (status === "Received") {
            ws[cellAddress].s.font = { ...ws[cellAddress].s.font, color: { rgb: "008000" }, bold: true };
          } else if (status === "Pending") {
            ws[cellAddress].s.font = { ...ws[cellAddress].s.font, color: { rgb: "FFA500" }, bold: true };
          } else if (status === "Forwarded") {
            ws[cellAddress].s.font = { ...ws[cellAddress].s.font, color: { rgb: "0000FF" }, bold: true };
          }
        }
        
        // Alternate row coloring
        if (R % 2 === 0) {
          ws[cellAddress].s.fill = {
            fgColor: { rgb: "F2F2F2" } // Light gray for even rows
          };
        }
      }
    }
  }

  // Freeze header row
  ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2", activePane: "bottomRight" };

  // Add filters to header row
  ws["!autofilter"] = { ref: XLSX.utils.encode_range(range) };

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, "File Details");

  // Add metadata
  wb.Props = {
    Title: "File Details Report",
    Subject: "File Information",
    Author: "Your Application",
    CreatedDate: new Date(),
    Company: "Your Company Name"
  };

  // Generate Excel file
  const excelBuffer = XLSX.write(wb, {
    bookType: "xlsx",
    type: "array",
    bookSST: true,
    cellStyles: true
  });

  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });

  saveAs(blob, `File_Details_Report_${new Date().toISOString().slice(0, 19).replace(/[:T-]/g, '')}.xlsx`);
};

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleReset = () => {
    formik.resetForm({
      values: {
        fileID: -1,
        fileType: -1,
        chackNo: "",
        fileGroup: -1,
        fileCategory: -1,
        fileClass: -1,
        fileSubject: -1,
          "zoneID": -1,
  "wardID": -1,
  "rfid": "",
  "user_ID": ""

      },
    });
    setData([]);
    setHasSearched(false);
    setPage(0);
  };

  return (
    <Card
      sx={{
        width: "100%",
        boxShadow: theme.shadows[4],
        borderRadius: "12px",
        overflow: "hidden",
        marginTop: "24px",
      }}
    >
         <ToastContainer></ToastContainer>
      <Box
        sx={{
          p: 3,
          backgroundColor: "background.paper",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <DescriptionIcon
                sx={{
                  color: "primary.main",
                  fontSize: 32,
                }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                {t("text.FileDetailsReport")}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
           <Grid container spacing={2} justifyContent="flex-end">
  <Grid item>
    <Button
      variant="contained"
      startIcon={<FilterAltIcon />}
      onClick={toggleFilters}
      sx={{
        fontWeight: 600,
        textTransform: "none",
        borderRadius: "6px",
        px: 3,
        py: 1,
      }}
    >
      {t("text.Filter")}
    </Button>
  </Grid>
  <Grid item>
    <Button
      variant="outlined"
      startIcon={<DescriptionIcon />}
      onClick={exportToPDF}
      sx={{
        fontWeight: 600,
        textTransform: "none",
        borderRadius: "6px",
        px: 3,
        py: 1,
        borderWidth: "2px",
        "&:hover": {
          borderWidth: "2px",
        },
      }}
    >
      {t("text.PDF")}
    </Button>
  </Grid>
  <Grid item>
    <Button
      variant="outlined"
      startIcon={<DescriptionIcon />}
      onClick={exportToExcel}
      sx={{
        fontWeight: 600,
        textTransform: "none",
        borderRadius: "6px",
        px: 3,
        py: 1,
        borderWidth: "2px",
        "&:hover": {
          borderWidth: "2px",
        },
      }}
    >
      {t("text.Excel")}
    </Button>
  </Grid>
  
</Grid>
          </Grid>
        </Grid>
      </Box>

      {showFilters && (
        <Box
          sx={{
            p: 3,
            backgroundColor: "background.default",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  disablePortal
                  id="file-select"
                  options={optionFileDetailsAll}
                  fullWidth
                  size="small"
                  value={
                    optionFileDetailsAll.find(
                      (option) => option.value === formik.values.fileID
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue("fileID", newValue?.value || -1);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("text.SelectFile")}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
                 <Grid item xs={12} sm={6} md={4} lg={3}>
                              <Autocomplete
                                disablePortal
                                id="rfid-select"
                                options={optionRefid}
                                fullWidth
                                size="small"
                                value={
                                  optionRefid.find(
                                    (option: any) => option.value === formik.values.rfid
                                  ) || null
                                }
                                onChange={(event, newValue: any) => {
                                  formik.setFieldValue("rfid", newValue?.value || "");
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={t("text.SelectRFID")}
                                    variant="outlined"
                                  />
                                )}
                              />
                            </Grid>

                                   
                <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  disablePortal
                  id="zone-select"
                  options={ZoneOption.filter((option: any) => option.value !== "-1")}
                  fullWidth
                  size="small"
                  value={
                  ZoneOption.find(
                    (option: any) => option.value === formik.values.zoneID
                  ) || null
                  }
                  onChange={handleZoneChange}
                  renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                    <span>
                      {t("text.SelectZoneName")}
                    </span>
                    }
                  />
                  )}
                />
                {formik.touched.zoneID && formik.errors.zoneID ? (
                  <div style={{ color: "red", margin: "5px" }}>
                  {/* {formik.errors.zoneID} */}
                  </div>
                ) : null}
                </Grid>
                            
                                         
                <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  disablePortal
                  id="combo-box-demo"
                  options={WardOption.filter((option: any) => option.value !== "-1")}
                  fullWidth
                  size="small"
                  value={
                    WardOption.find(
                      (option: any) => option.value === formik.values.wardID
                    ) || null
                  }
                  onChange={(_, newValue) => {
                    formik.setFieldValue("wardID", newValue?.value ?? -1);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        <span>
                          {t("text.SelectWardName")}
                        </span>
                      }
                    />
                  )}
                />
                {formik.touched.wardID && formik.errors.wardID ? (
                  <div style={{ color: "red", margin: "5px" }}>
                  {/* {formik.errors.wardID} */}
                  </div>
                ) : null}
                </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  disablePortal
                  id="chack-select"
                  options={optionchackNo}
                  fullWidth
                  size="small"
                  value={
                    optionchackNo.find(
                      (option) => option.label === formik.values.chackNo
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue("chackNo", newValue?.label || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("text.Selectchack")}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  disablePortal
                  id="file-type-select"
                  options={optionFileType}
                  fullWidth
                  size="small"
                  value={
                    optionFileType.find(
                      (option) => option.value === formik.values.fileType
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue("fileType", newValue?.value || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("text.SelectFileType")}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  disablePortal
                  id="file-group-select"
                  options={optionFileGroup}
                  fullWidth
                  size="small"
                  value={
                    optionFileGroup.find(
                      (option) => option.value === formik.values.fileGroup
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue("fileGroup", newValue?.value || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("text.SelectFileGroup")}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  disablePortal
                  id="file-category-select"
                  options={optionFileCategory}
                  fullWidth
                  size="small"
                  value={
                    optionFileCategory.find(
                      (option) => option.value === formik.values.fileCategory
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue("fileCategory", newValue?.value || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("text.SelectFileCategory")}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  disablePortal
                  id="file-class-select"
                  options={optionFileClass}
                  fullWidth
                  size="small"
                  value={
                    optionFileClass.find(
                      (option) => option.value === formik.values.fileClass
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue("fileClass", newValue?.value || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("text.SelectFileClass")}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  disablePortal
                  id="file-subject-select"
                  options={optionFilesubject}
                  fullWidth
                  size="small"
                  value={
                    optionFilesubject.find(
                      (option) => option.value === formik.values.fileSubject
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue("fileSubject", newValue?.value || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("text.SelectFileSubject")}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                      sx={{
                        height: "40px",
                        borderRadius: "6px",
                        fontWeight: 600,
                      }}
                      disabled={loading}
                    >
                      {loading ? (
                        <CircularProgress size={24} />
                      ) : (
                        t("text.search")
                      )}
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      type="button"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      sx={{
                        height: "40px",
                        borderRadius: "6px",
                        fontWeight: 600,
                        borderWidth: "2px",
                        "&:hover": {
                          borderWidth: "2px",
                        },
                      }}
                      onClick={handleReset}
                      disabled={loading}
                    >
                      {t("text.Clear")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </Box>
      )}

      <Box sx={{ p: 3 }}>
        <TableContainer
          component={Paper}
          id="tabcont"
          sx={{
            borderRadius: "8px",
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[1],
            overflow: "auto",
            maxHeight: "calc(100vh - 300px)",
          }}
        >
          <Table
            aria-label="file details table"
            stickyHeader
            sx={{
              minWidth: 650,
              "& .MuiTableCell-root": {
                padding: "12px 16px",
              },
            }}
          >
<TableHead>
  <TableRow>
    <StyledTableCell align="center">{t("text.SrNo")}</StyledTableCell>
    <StyledTableCell>{t("text.FileName")}</StyledTableCell>
    <StyledTableCell align="center">{t("text.FileNo")}</StyledTableCell>
    <StyledTableCell align="center">{t("text.chackNo")}</StyledTableCell>
    <StyledTableCell align="center">{t("text.RFID")}</StyledTableCell>
    <StyledTableCell align="center">{t("text.Status")}</StyledTableCell>
    <StyledTableCell align="center">{t("text.Remark")}</StyledTableCell>
    {/* Add the new Preview column */}
    <StyledTableCell align="center">{t("text.PreviewFile")}</StyledTableCell>
  </TableRow>
</TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : data.length > 0 ? (
                (rowsPerPage > 0
                  ? data.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                  : data
                ).map((row: any, index: any) => (
                  <Row
                    key={index}
                    row={row}
                    index={page * rowsPerPage + index}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      {hasSearched
                        ? t("text.Nodatafoundfortheselectedfilters")
                        : t("text.Pleaseapplyfiltersandclicksearchtoviewdata")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={7} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderTop: 0,
            borderBottomLeftRadius: "8px",
            borderBottomRightRadius: "8px",
          }}
        >
          <Box sx={{ flex: 1 }} />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
            colSpan={3}
            count={data.length}
            rowsPerPage={rowsPerPage}
            page={page}
            SelectProps={{
              inputProps: {
                "aria-label": "rows per page",
              },
              native: true,
            }}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
            sx={{
              border: "none",
              "& .MuiTablePagination-toolbar": {
                padding: 0,
              },
              "& .MuiTablePagination-selectLabel": {
                margin: 0,
              },
              "& .MuiTablePagination-displayedRows": {
                margin: 0,
              },
            }}
          />
        </Paper>
      </Box>

      <iframe
        id="ifmcontentstoprint"
        style={{ height: "0px", width: "0px", position: "absolute" }}
      />
      <PDFPreviewModal
  open={pdfModalOpen}
  onClose={() => setPdfModalOpen(false)}
  pdfBlob={pdfBlob}
  onDownload={pdfDownloadFn || (() => {})}
/>
    </Card>
  );
}








// import React, { useState, useEffect } from "react";
// import {
//   Button,
//   Card,
//   Divider,
//   Paper,
//   Grid,
//   SwipeableDrawer,
//   Typography,
//   TextField,
// } from "@mui/material";
// import FilterAltIcon from "@mui/icons-material/FilterAlt";
// import Table from "@mui/material/Table";
// import TableBody from "@mui/material/TableBody";
// import TableCell, { tableCellClasses } from "@mui/material/TableCell";
// import TableContainer from "@mui/material/TableContainer";
// import TableHead from "@mui/material/TableHead";
// import TableRow from "@mui/material/TableRow";
// import { toast } from "react-toastify";
// import { useFormik } from "formik";
// import Autocomplete from "@mui/material/Autocomplete";
// import { useTranslation } from "react-i18next";
// import PrintIcon from "@mui/icons-material/Print";
// import { styled } from "@mui/material/styles";
// import api from "../../../utils/Url";

// const StyledTableCell = styled(TableCell)(({ theme }) => ({
//   [`&.${tableCellClasses.head}`]: {
//     backgroundColor: "#00009C",
//     color: theme.palette.common.white,
//   },
//   [`&.${tableCellClasses.body}`]: {
//     fontSize: 14,
//   },
// }));

// const StyledTableRow = styled(TableRow)(({ theme }) => ({
//   "&:nth-of-type(odd)": {
//     backgroundColor: theme.palette.action.hover,
//   },
//   // hide last border
//   "&:last-child td, &:last-child th": {
//     border: 0,
//   },
// }));

// function Row({ row, index }: { row: any; index: number }) {
//   const [isOpen, setIsOpen] = React.useState(false);
//   const [isHovered, setIsHovered] = useState(false);

//   return (
//     <React.Fragment>
//       <StyledTableRow sx={{ border: "1px gray solid", padding: "5px" }}>
//         <TableCell style={{ border: "1px gray solid" }} align="center">
//           {index + 1}
//         </TableCell>
//         <TableCell style={{ border: "1px gray solid" }}>
//           {row.fileName}
//         </TableCell>
//         <TableCell style={{ border: "1px gray solid" }} align="center">
//           {row.fileNo}
//         </TableCell>
//         {/* <TableCell style={{ border: "1px gray solid" }}  align="center">
//             {row.synopsis}
//           </TableCell> */}
//         <TableCell style={{ border: "1px gray solid" }} >
//           {row.chackNo}
//         </TableCell>
//         <TableCell style={{ border: "1px gray solid" }} align="center">
//           {row.rfid}
//         </TableCell>
//         <TableCell style={{ border: "1px gray solid" }} align="center">
//           {row.status}
//         </TableCell>
//         <TableCell style={{ border: "1px gray solid" }} align="center">
//           {row.remark}
//         </TableCell>
//       </StyledTableRow>
//     </React.Fragment>
//   );
// }

// export default function FileDetailsReports() {
//   const { t } = useTranslation();



//   const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
//   const [data, setData] = useState<any>([]);

//   const [optionFileCategory, setOptionFileCategory] = useState([
//     { value: "-1", label: t("text.SelectFileCategory") },
//   ]);
//   const [optionFileGroup, setOptionFileGroup] = useState([
//     { value: "-1", label:  t("text.SelectFileGroup") },
//   ]);
//   const [optionFileClass, setOptionFileClass] = useState([
//     { value: "-1", label:  t("text.SelectFileClass") },
//   ]);
//   const [optionFilesubject, setOptionFilesubject] = useState([
//     { value: "-1", label:  t("text.SelectFileSubject") },
//   ]);
//   const [optionFileType, setOptionFileType] = useState([
//     { value: "-1", label:  t("text.SelectFileType") },
//   ]);
//   const [optionFileDetailsAll, setOptionFileDetailsAll] = useState([
//     { value: "-1", label: t("text.SelectFile") },
//   ]);
//   const [optionchackNo, setOptionchackNo] = useState([
//     { label: t("text.Selectchack") },
//   ]);

 

//   useEffect(() => {
//     getFileCategoryData();
//     getFileGroupData();
//     getFileClassData();
//     getFileSubjectData();
//     getFileTypeData();
//     getFileDtailsAllData();
//     getchackData();
//   }, []);

//   const getchackData = () => {

//     api
//       .post( "FileDetail/GetGroupWiseCheckNo")
//       .then((res) => {
//         const arr = [];
//         console.log("result" + JSON.stringify(res.data));
//         for (let index = 0; index < res.data.data.length; index++) {
//           arr.push({
//             label: res.data.data[index]["chackNo"]
//           });
//         }
//         setOptionchackNo(arr);
//       });
//   };

//   const getFileCategoryData = () => {
//     const collectData = {
//       fileCatid: -1,
//     };

//     api
//       .post("FileCategory/GetFileCategory", collectData)
//       .then((res) => {
//         const arr = [];
//         console.log("result" + JSON.stringify(res.data));
//         for (let index = 0; index < res.data.data.length; index++) {
//           arr.push({
//             label: res.data.data[index]["fileCatDesc"],
//             value: res.data.data[index]["fileCatid"],
//           });
//         }
//         setOptionFileCategory(arr);
//       });
//   };

//   const getFileGroupData = () => {
//     const collectData = {
//       fileGroupid: -1,
//     };

//     api
//       .post( "FileGroup/GetFileGroup", collectData)
//       .then((res) => {
//         const arr = [];
//         console.log("result" + JSON.stringify(res.data));
//         for (let index = 0; index < res.data.data.length; index++) {
//           arr.push({
//             label: res.data.data[index]["fileGroupName"],
//             value: res.data.data[index]["fileGroupid"],
//           });
//         }
//         setOptionFileGroup(arr);
//       });
//   };

//   const getFileClassData = () => {
//     const collectData = {
//       fileClassid: -1,
//     };

//     api
//       .post( "FileClass/GetFileClass", collectData)
//       .then((res) => {
//         const arr = [];
//         console.log("result" + JSON.stringify(res.data));
//         for (let index = 0; index < res.data.data.length; index++) {
//           arr.push({
//             label: res.data.data[index]["classDescription"],
//             value: res.data.data[index]["fileClassid"],
//           });
//         }
//         setOptionFileClass(arr);
//       });
//   };

//   const getFileSubjectData = () => {
//     const collectData = {
//       id: -1,
//     };

//     api
//       .post( "FileSubject/GetFileSubject", collectData)
//       .then((res) => {
//         const arr = [];
//         console.log("result" + JSON.stringify(res.data));
//         for (let index = 0; index < res.data.data.length; index++) {
//           arr.push({
//             label: res.data.data[index]["fileSubjectName"],
//             value: res.data.data[index]["id"],
//           });
//         }
//         setOptionFilesubject(arr);
//       });
//   };

//   const getFileTypeData = () => {
//     const collectData = {
//       fileTypeId: -1,
//     };

//     api
//       .post( "FileType/GetFileType", collectData)
//       .then((res) => {
//         const arr = [];
//         console.log("result" + JSON.stringify(res.data));
//         for (let index = 0; index < res.data.data.length; index++) {
//           arr.push({
//             label: res.data.data[index]["fileTypeName"],
//             value: res.data.data[index]["fileTypeId"],
//           });
//         }
//         setOptionFileType(arr);
//       });
//   };

//   const getFileDtailsAllData = () => {
//     const collectData = {
//       fileID: -1,
//       fileType: -1,
//       fileGroup: -1,
//       fileCategory: -1,
//       fileClass: -1,
//       fileSubject: -1,
//     };

//     api
//       .post( "FileDetail/GetFileDetails", collectData)
//       .then((res) => {
//         const arr = [];
//         console.log("result" + JSON.stringify(res.data));
//         for (let index = 0; index < res.data.data.length; index++) {
//           arr.push({
//             label: res.data.data[index]["fileName"],
//             value: res.data.data[index]["fileID"],
//           });
//         }
//         setOptionFileDetailsAll(arr);
//       });
//   };

//   const handleFilterButtonClick = () => {
//     setFilterDrawerOpen(true);
//     console.log("value handleFilterButtonClick", filterDrawerOpen);
//   };

//   const handleCloseDrawer = () => {
//     setFilterDrawerOpen(false);
//   };

//   interface FormValues {
//     fileID: any;
//     fileType: any;
//     chackNo:string;
//     fileGroup: any;
//     fileCategory: any;
//     fileClass: any;
//     fileSubject: any;
//   }

//   const initialValues: FormValues = {
//     fileID: "",
//     fileType: "",
//     chackNo:"",
//     fileGroup: "",
//     fileCategory: "",
//     fileClass: "",
//     fileSubject: "",
//   };

//   const formik = useFormik({
//     initialValues: initialValues,

//     onSubmit: async (values) => {
//       console.log("Form values:", formik.values);

//       if (values.fileID === initialValues.fileID) {
//         values.fileID = -1;
//       }
//       if (values.fileType === initialValues.fileType) {
//         values.fileType = -1;
//       }
//       if (values.fileGroup === initialValues.fileGroup) {
//         values.fileGroup = -1;
//       }
//       if (values.fileCategory === initialValues.fileCategory) {
//         values.fileCategory = -1;
//       }
//       if (values.fileClass === initialValues.fileClass) {
//         values.fileClass = -1;
//       }
//       if (values.fileSubject === initialValues.fileSubject) {
//         values.fileSubject = -1;
//       }
//       if (values.chackNo === initialValues.chackNo) {
//         values.chackNo = "";
//       }
     

//       console.log("check data before submitting", values);

//       const response = await api.post(
//          `FileDetail/GetFileDetails`,
//         values
//       );
//       setData(response.data.data);
//       handleCloseDrawer();
//       if (response.data.isSuccess) {
//         toast.success(response.data.mesg);
//       } else {
//         toast.error(response.data.mesg);
//       }
//     },
//   });

//   const handlePrint = () => {
//     const contentElement = document.getElementById("tabcont");
//     if (contentElement) {
//       const clonedContent = contentElement.cloneNode(true) as HTMLElement;

//       const filterButton = clonedContent.querySelector(".filter-button");
//       if (filterButton) {
//         filterButton.remove();
//       }
//       const printButton = clonedContent.querySelector(".print-button");
//       if (printButton) {
//         printButton.remove();
//       }

//       const printWindow = window.open("", "_blank");
//       if (printWindow) {
//         printWindow.document.body.appendChild(clonedContent);

//         printWindow.print();
//       } else {
//         console.error("Error: Unable to open print window.");
//       }
//     }
//   };

//   return (
//     <Card
//       style={{
//         width: "100%",
//         backgroundColor: "#E9FDEE",
//         border: ".5px solid #fff ",
//         marginTop: "3vh",
//       }}
//     >
//       <Paper
//         sx={{
//           width: "100%",
//           overflow: "hidden",
//           "& .MuiDataGrid-colCell": {
//             backgroundColor: "#f0ad4e",
//             color: "#000",
//             fontSize: 17,
//             fontWeight: 900,
//           },
//         }}
//         style={{ padding: "10px" }}
//       >
//         <div
//           style={{
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "space-between",
//           }}
//         >
//           <Typography
//             gutterBottom
//             variant="h5"
//             component="div"
//             sx={{ padding: "20px" }}
//             align="left"
//           >
//             {t("text.FileDetailsReport")}
//           </Typography>

//           <div
//             style={{
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "space-between",
//               gap: 20,
//             }}
//           >
//             <Button
//               type="button"
//               onClick={() => handleFilterButtonClick()}
//               style={{
//                 backgroundColor: "blue",
//                 fontSize: "15px",
//                 color: "#fff",
//                 textTransform: "none",
//                 cursor: "pointer",
//               }}
//             >
//               <FilterAltIcon />
//               {t("text.Filter")}
//             </Button>
//             <Button
//               type="button"
//               onClick={handlePrint}
//               style={{
//                 backgroundColor: "blue",
//                 fontSize: "15px",
//                 color: "#fff",
//                 textTransform: "none",
//                 cursor: "pointer",
//               }}
//             >
//               <PrintIcon />
//               {t("text.Print")}
//             </Button>
//             <iframe
//               id="ifmcontentstoprint"
//               style={{ height: "0px", width: "0px", position: "absolute" }}
//             ></iframe>
//           </div>
//           <SwipeableDrawer
//             anchor="right"
//             open={filterDrawerOpen}
//             onClose={handleCloseDrawer}
//             onOpen={() => {}}
//             slotProps={{
//               backdrop: {
//                 style: { backgroundColor: "rgba(0, 0, 0, 0.7)" },
//               },
//             }}
//             PaperProps={{
//               style: {
//                 width: 310,
//                 boxShadow: "0px 0px 8px 0px rgba(0,0,0,0.2)",
//               },
//             }}
//             style={{
//               zIndex: 1300,
//             }}
//           >
//             <Typography
//               gutterBottom
//               variant="h5"
//               component="div"
//               sx={{
//                 margin: "20px 0px",
//               }}
//               align="center"
//             >
//               {t("text.FileDetailsReport")}
//             </Typography>
//             <Divider />

//             <div style={{ margin: "20px 0px" }}>
//               <form onSubmit={formik.handleSubmit}>
//                 <Grid
//                   container
//                   spacing={2}
//                   sx={{
//                     display: "flex",
//                     flexDirection: "row",
//                     gap: 2,
//                     marginLeft: "0px",
//                   }}
//                 >
//                   <Grid xs={11} sm={11} item>
//                     <Autocomplete
//                       disablePortal
//                       id="combo-box-demo"
//                       options={optionFileDetailsAll}
//                       fullWidth
//                       size="small"
//                       onChange={(event, newValue) => {
//                         console.log(newValue?.value);

//                         formik.setFieldValue("fileID", newValue?.value );
//                         formik.setFieldTouched("fileID", true);
//                         formik.setFieldTouched("fileID", false);
//                       }}
//                       renderInput={(params) => (
//                         <TextField {...params} label={t("text.SelectFile")} />
//                       )}
//                     />
//                   </Grid>

//                   <Grid xs={11} sm={11} item>
//                     <Autocomplete
//                       disablePortal
//                       id="combo-box-demo"
//                       options={optionchackNo}
//                       fullWidth
//                       size="small"
//                       onChange={(event, newValue) => {
//                         console.log(newValue?.label);

//                         formik.setFieldValue("chackNo", newValue?.label );
//                         formik.setFieldTouched("chackNo", true);
//                         formik.setFieldTouched("chackNo", false);
//                       }}
//                       renderInput={(params) => (
//                         <TextField {...params} label={t("text.Selectchack")} />
//                       )}
//                     />
//                   </Grid>

//                   <Grid xs={11} sm={11} item>
//                     <Autocomplete
//                       disablePortal
//                       id="combo-box-demo"
//                       options={optionFileType}
//                       fullWidth
//                       size="small"
//                       onChange={(event, newValue) => {
//                         console.log(newValue?.value);
//                         formik.setFieldValue("fileType", newValue?.value);
//                         formik.setFieldTouched("fileType", true);
//                         formik.setFieldTouched("fileType", false);
//                       }}
//                       renderInput={(params) => (
//                         <TextField {...params} label={t("text.SelectFileType")} />
//                       )}
//                     />
//                   </Grid>

//                   <Grid xs={11} sm={11} item>
//                     <Autocomplete
//                       disablePortal
//                       id="combo-box-demo"
//                       options={optionFileGroup}
//                       fullWidth
//                       size="small"
//                       onChange={(event, newValue) => {
//                         console.log(newValue?.value);

//                         formik.setFieldValue("fileGroup", newValue?.value);
//                         formik.setFieldTouched("fileGroup", true);
//                         formik.setFieldTouched("fileGroup", false);
//                       }}
//                       renderInput={(params) => (
//                         <TextField {...params} label={t("text.SelectFileGroup")} />
//                       )}
//                     />
//                   </Grid>

                  // <Grid xs={11} sm={11} item>
                  //   <Autocomplete
                  //     disablePortal
                  //     id="combo-box-demo"
                  //     options={optionFileCategory}
                  //     fullWidth
                  //     size="small"
                  //     onChange={(event, newValue) => {
                  //       console.log(newValue?.value);

                  //       formik.setFieldValue("fileCategory", newValue?.value);
                  //       formik.setFieldTouched("fileCategory", true);
                  //       formik.setFieldTouched("fileCategory", false);
                  //     }}
                  //     renderInput={(params) => (
                  //       <TextField {...params} label={t("text.SelectFileCategory")} />
                  //     )}
                  //   />
                  // </Grid>

                  // <Grid xs={11} sm={11} item>
                  //   <Autocomplete
                  //     disablePortal
                  //     id="combo-box-demo"
                  //     options={optionFileClass}
                  //     fullWidth
                  //     size="small"
                  //     onChange={(event, newValue) => {
                  //       console.log(newValue?.value);

                  //       formik.setFieldValue("fileClass", newValue?.value);
                  //       formik.setFieldTouched("fileClass", true);
                  //       formik.setFieldTouched("fileClass", false);
                  //     }}
                  //     renderInput={(params) => (
                  //       <TextField {...params} label={t("text.SelectFileClass")} />
                  //     )}
                  //   />
                  // </Grid>

                  // <Grid xs={11} sm={11} item>
                  //   <Autocomplete
                  //     disablePortal
                  //     id="combo-box-demo"
                  //     options={optionFilesubject}
                  //     defaultValue={null}
                  //     fullWidth
                  //     size="small"
                  //     onChange={(event, newValue) => {
                  //       console.log(newValue?.value);

                  //       formik.setFieldValue("fileSubject", newValue?.value);
                  //       formik.setFieldTouched("fileSubject", true);
                  //       formik.setFieldTouched("fileSubject", false);
                  //     }}
                  //     renderInput={(params) => (
                  //       <TextField {...params} label={t("text.SelectFileSubject")} />
                  //     )}
                  //   />
                  // </Grid>

//                   <Grid xs={10} item>
//                     <div style={{ justifyContent: "space-between", flex: 1 }}>
//                       <Button
//                         type="submit"
//                         variant="contained"
//                         style={{
//                           width: "37%",
//                           // backgroundColor: "#059669",
//                           margin: "0.5%",
//                         }}
//                       >
//                         {t("text.search")}
//                       </Button>
//                       <Button
//                         type="reset"
//                         variant="contained"
//                         style={{
//                           width: "37%",
//                           // backgroundColor: "#F43F5E",
//                           margin: "0.5%",
//                         }}
//                         onClick={() => {
//                           formik.resetForm();

//                           setOptionFileClass([
//                             { value: "-1", label: t("text.SelectFileClass") },
//                           ]);
//                         }}
//                       >
//                         {t("text.Clear")}
//                       </Button>
//                     </div>
//                   </Grid>
//                 </Grid>
//               </form>
//             </div>
//           </SwipeableDrawer>
//         </div>

//         <Divider />
//         <br />

//         <TableContainer
//           component={Paper}
//           id="tabcont"
//           sx={{
//             maxHeight: "65vh",
//             marginBottom: "10px",
//             border: "1px solid #fff",
//           }}
//         >
//           <Table
//             aria-label="customized  table"
//             style={{
//               border: "1px gray solid",
//               borderCollapse: "collapse",
//               width: "100%",
//             }}
//           >
//             <TableHead
//               style={{
//                 border: "1px gray solid",
//                 borderCollapse: "collapse",
//                 position: "sticky",
//                 top: 0, 
//                 zIndex: 1, 
//                 backgroundColor: "#fff",
//               }}
//             >
//               <StyledTableCell
//                 colSpan={10}
//                 align="center"
//                 style={{
//                   fontSize: 17,
//                   fontWeight: 600,
//                   border: "1px gray solid",
//                   padding: "5px",
//                   backgroundColor: "transparent",
//                   color: "#000",
//                 }}
//               >
//                 {t("text.FileDetailsReport")}
//               </StyledTableCell>
//               <TableRow>
//                 {/* <StyledTableCell /> */}
//                 <StyledTableCell
//                   align="center"
//                   style={{
//                     fontSize: 17,
//                     fontWeight: 500,
//                     border: "1px gray solid",
//                     padding: "10px",
//                   }}
//                 >
//                   {t("text.SrNo")}
//                 </StyledTableCell>
//                 <StyledTableCell
//                   align="center"
//                   style={{
//                     fontSize: 17,
//                     fontWeight: 500,
//                     border: "1px gray solid",
//                     padding: "10px",
//                   }}
//                 >
//                   {t("text.FileName")}
//                 </StyledTableCell>
//                 <StyledTableCell
//                   align="center"
//                   style={{
//                     fontSize: 17,
//                     fontWeight: 500,
//                     border: "1px gray solid",
//                     padding: "10px",
//                   }}
//                 >
//                   {t("text.FileNo")}
//                 </StyledTableCell>
//                 <StyledTableCell
//                   align="center"
//                   style={{
//                     fontSize: 17,
//                     fontWeight: 500,
//                     border: "1px gray solid",
//                     padding: "10px",
//                   }}
//                 >
//                   {t("text.chackNo")}
//                 </StyledTableCell>
//                 <StyledTableCell
//                   align="center"
//                   style={{
//                     fontSize: 17,
//                     fontWeight: 500,
//                     border: "1px gray solid",
//                     padding: "10px",
//                   }}
//                 >
//                   {t("text.RFID")}
//                 </StyledTableCell>
//                 <StyledTableCell
//                   align="center"
//                   style={{
//                     fontSize: 17,
//                     fontWeight: 500,
//                     border: "1px gray solid",
//                     padding: "10px",
//                   }}
//                 >
//                   {t("text.Status")}
//                 </StyledTableCell>
//                 <StyledTableCell
//                   align="center"
//                   style={{
//                     fontSize: 17,
//                     fontWeight: 500,
//                     border: "1px gray solid",
//                     padding: "10px",
//                   }}
//                 >
//                   {t("text.Remark")}
//                 </StyledTableCell>
//               </TableRow>
//             </TableHead>
//            {data && <TableBody>
//               {data.map((row: any, index: any) => (
//                 <Row key={row.any} row={row} index={index} />
//               ))}
//             </TableBody>}
//           </Table>
//         </TableContainer>
//       </Paper>
//     </Card>
//   );
// }


